import { RefObject } from 'react';
import { AffiliateDetails } from 'utils/affiliateHelpers';
import {
  RingProColors,
  RingProChargerCaseColor,
  RingSizes,
} from 'content/products/ring-pro';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { TradeInDevice } from 'content/products/tradeIn';
import type { addToCartArgsType } from 'components/shared/Cart/types';
import { TierCampaignData, TierInfo } from 'content/products/ring-pro/tierCampaignTypes';
export type { TierCampaignData, TierInfo };

export type addonsType = 'Year2' | 'Year1' | false | null;

/** Server-driven discount info returned from token validation API */
export type ServerDiscountInfo = {
  applicable: boolean;
  code?: string;
  discountedPrice?: number;
  label?: string;
  priceTagText?: string;
};

/** Enhanced effective discount with server-provided metadata */
export type EffectiveDiscount = {
  type: string;
  amount: number;
  /** Tier/server discount portion (already baked into `price`). */
  serverAmount: number;
  /** Affiliate discount portion (applied on top of server discount). */
  affiliateAmount: number;
  label?: string;
  priceTagText?: string;
  code?: string;
  affiliateCode?: string;
};

export type RingProContextType = {
  color: RingProColors | null;
  setColor: (color: RingProColors) => void;

  size: RingSizes | null;
  setSize: (size: RingSizes) => void;

  chargerCaseColor: RingProChargerCaseColor | null;
  setChargerCaseColor: (c: RingProChargerCaseColor) => void;

  defaultCurrency: Currency;
  currencySymbol?: React.ReactNode;

  uhx: boolean;
  setUhx: (value: boolean) => void;

  engraving: boolean;
  setEngraving: (value: boolean) => void;
  engravingMessage: string | null;
  setEngravingMessage: (value: string | null) => void;

  checkout: () => Promise<void>;
  // Line items for the current selection, used by the Apple Pay express button
  // to build an isolated buy-now cart. Mirrors what checkout() adds.
  getExpressCheckoutLineItems: () => addToCartArgsType[];
  isLoading: boolean;

  uhxCart: addonsType;
  setUhxCart: (value: addonsType) => void;

  afibCart: addonsType;
  setAfibCart: (value: addonsType) => void;

  cardioAdaptibilityCart: addonsType;
  setCardioAdaptabilityCart: (value: addonsType) => void;

  cnoProCart: addonsType;
  setCnoProCart: (value: addonsType) => void;

  clueCart: addonsType;
  setClueCart: (value: addonsType) => void;

  respiratoryCart: addonsType;
  setRespiratoryCart: (value: addonsType) => void;

  store: ShopifyStore | null;
  serverDiscount: ServerDiscountInfo;

  /** Base (struck-out) price. Same regardless of discount. */
  basePrice: number;
  /** Display price (discounted if server discount applicable, otherwise base). */
  price: number;
  ultraHumanPrice: number;
  effectivePrice: number;
  chargerPrice: number;
  shippingEstimate: string | React.ReactNode | null;
  affiliateDetails: AffiliateDetails | null;
  campaignActive: boolean;
  affiliateDiscount: number;
  referral?: { discountCode: string; discount: number };
  showEngravingSection: boolean;
  priceDrop: number;
  effectiveDiscount: EffectiveDiscount;

  sectionRefs: {
    showSizeRef?: RefObject<HTMLDivElement | null>;
    showChargerRef?: RefObject<HTMLDivElement | null>;
    engravingRef?: RefObject<HTMLDivElement | null>;
    coverageRef?: RefObject<HTMLDivElement | null>;
    powerplugRef?: RefObject<HTMLDivElement | null>;
    cartSummaryRef?: RefObject<HTMLDivElement | null>;
  };

  goToNextStep: () => void;
  notServicable?: boolean;
  notServicableMessage?: string;
  uhxFirstOnly?: boolean;
  isOpenSizeDisabled?: boolean;

  tradeInDevice: TradeInDevice | null;
  setTradeInDevice: (device: TradeInDevice | null) => void;
  tradeInSerialNumber: string;
  setTradeInSerialNumber: (value: string) => void;
  tradeInCredit: number;
  tradeInMaxCredit: number;
  hasUhx: boolean;
  tierCampaign: TierCampaignData | null;
};

export type ShippingEstimateObject = Record<
  string,
  {
    date: string;
    mcf_for_eta: boolean;
    show_mcf_logo: boolean;
    default_custom_eta: string;
  }
>;

export type AddonsState = {
  engraving: { enabled: boolean; message: string | null };
  uhxCart: addonsType;
  afibCart: addonsType;
  cardioAdaptibilityCart: addonsType;
  cnoProCart: addonsType;
  clueCart: addonsType;
  respiratoryCart: addonsType;
  tradeIn: { device: TradeInDevice | null; serialNumber: string };
};

export type AddonsAction =
  | { type: 'SET_ENGRAVING'; payload: boolean }
  | { type: 'SET_ENGRAVING_MESSAGE'; payload: string | null }
  | { type: 'SET_UHX_CART'; payload: addonsType }
  | { type: 'SET_AFIB_CART'; payload: addonsType }
  | { type: 'SET_CARDIO_ADAPTIBILITY_CART'; payload: addonsType }
  | { type: 'SET_CNO_PRO_CART'; payload: addonsType }
  | { type: 'SET_CLUE_CART'; payload: addonsType }
  | { type: 'SET_RESPIRATORY_CART'; payload: addonsType }
  | { type: 'SET_TRADE_IN_DEVICE'; payload: TradeInDevice | null }
  | { type: 'SET_TRADE_IN_SERIAL_NUMBER'; payload: string };

export const initialAddonsStateRingPro: AddonsState = {
  engraving: { enabled: false, message: null },
  uhxCart: null,
  afibCart: null,
  cardioAdaptibilityCart: null,
  cnoProCart: null,
  clueCart: null,
  respiratoryCart: null,
  tradeIn: { device: null, serialNumber: '' },
};

export type CheckoutParamsRingPro = {
  color: RingProColors;
  size: RingSizes;
  store: ShopifyStore;
  chargerCaseColor: RingProChargerCaseColor;
  engraving: boolean;
  engravingMessage: string | null;
  eSizingSize?: boolean | string;
  uhxCart: addonsType;
  afibCart: addonsType;
  cardioAdaptibilityCart: addonsType;
  cnoProCart: addonsType;
  clueCart: addonsType;
  respiratoryCart: addonsType;
  serverDiscount: ServerDiscountInfo;
  affiliateDiscountCode?: string;
  isCnOPlus?: boolean;
  tradeInDevice: TradeInDevice | null;
  tradeInSerialNumber: string;
};
